import { createFileRoute, Link } from '@tanstack/react-router';
import React, { useState, useMemo } from 'react';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { PageLayout } from '../components/layout/PageLayout';

import { FiArrowLeft, FiBell } from 'react-icons/fi';
import { CheckCheck } from 'lucide-react';

export const Route = createFileRoute('/projects/$projectId/notifications')({
  component: NotificationsPage,
});

interface Notification {
  id: string;
  type: 'Task' | 'Mention' | 'Inventory' | 'Overdue' | 'Invite' | 'Budget' | 'Milestone' | 'Update';
  title: string;
  description: string;
  project: string;
  time: string;
  isUnread: boolean;
  color: string;
}

const INITIAL_NOTIFICATIONS: Notification[] = [
  {
    id: '1',
    type: 'Task',
    title: 'Task assigned to you',
    description: "Lucas Smith assigned you to 'DJ for Security Team' in Summer Sound Clash 2026.",
    project: 'Summer Sound Clash 2026',
    time: '2 min ago',
    isUnread: true,
    color: 'bg-emerald-500/10 text-emerald-450 border border-emerald-500/20',
  },
  {
    id: '2',
    type: 'Mention',
    title: 'You were mentioned',
    description: "@you - Please review the budget breakdown before Tuesday's production meeting.",
    project: 'Summer Sound Clash 2026',
    time: '15 min ago',
    isUnread: true,
    color: 'bg-blue-500/10 text-blue-450 border border-blue-500/20',
  },
  {
    id: '3',
    type: 'Inventory',
    title: 'Inventory updated',
    description: 'Tunde Bello changed Pioneer CDJ-3000 Deck status from Available to Assigned.',
    project: 'Summer Sound Clash 2026',
    time: '1 hr ago',
    isUnread: true,
    color: 'bg-purple-500/10 text-purple-450 border border-purple-500/20',
  },
  {
    id: '4',
    type: 'Overdue',
    title: 'Task overdue',
    description: 'Book Hotel Accommodations was due Jun 11 and is now 26 days past its deadline.',
    project: 'Summer Sound Clash 2026',
    time: '2 days ago',
    isUnread: true,
    color: 'bg-rose-500/10 text-rose-455 border border-rose-500/20',
  },
  {
    id: '5',
    type: 'Invite',
    title: 'Project shared with you',
    description:
      "Emma Drew invited you to collaborate on 'Afrobeats World Tour' as Production Manager.",
    project: 'Afrobeats World Tour',
    time: '3 days ago',
    isUnread: false,
    color: 'bg-teal-500/10 text-teal-450 border border-teal-500/20',
  },
  {
    id: '6',
    type: 'Budget',
    title: 'Budget threshold reached',
    description:
      'Summer Sound Clash 2026 has reached 90% of its total budget allocation ($81,400 / $90,000).',
    project: 'Summer Sound Clash 2026',
    time: '5 days ago',
    isUnread: false,
    color: 'bg-amber-500/10 text-amber-450 border border-amber-500/20',
  },
  {
    id: '7',
    type: 'Task',
    title: 'Task assigned to you',
    description: "Emma Johnson assigned you to 'Verify Stage Layout Plans'.",
    project: 'Summer Sound Clash 2026',
    time: 'Yesterday',
    isUnread: false,
    color: 'bg-emerald-500/10 text-emerald-450 border border-emerald-500/20',
  },
  {
    id: '8',
    type: 'Milestone',
    title: 'Milestone updated',
    description:
      'Marketing Campaign Launch milestone is now In Progress in Summer Sound Clash 2026.',
    project: 'Summer Sound Clash 2026',
    time: 'Yesterday',
    isUnread: false,
    color: 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20',
  },
  {
    id: '9',
    type: 'Mention',
    title: 'You were mentioned',
    description: '@you - can you confirm the DMX layout route for the main stage lighting?',
    project: 'Afrobeats World Tour',
    time: '2 days ago',
    isUnread: false,
    color: 'bg-blue-500/10 text-blue-450 border border-blue-500/20',
  },
  {
    id: '10',
    type: 'Inventory',
    title: 'Inventory updated',
    description: 'Asheera Johnson added 3 items from account inventory to Summer Sound Clash 2026.',
    project: 'Summer Sound Clash 2026',
    time: '2 days ago',
    isUnread: false,
    color: 'bg-purple-500/10 text-purple-450 border border-purple-500/20',
  },
  {
    id: '11',
    type: 'Invite',
    title: 'Collaborator joined',
    description:
      'Nate Peterson accepted their invitation and joined the edit team as Videographer.',
    project: 'Summer Sound Clash 2026',
    time: '6 days ago',
    isUnread: false,
    color: 'bg-teal-500/10 text-teal-450 border border-teal-500/20',
  },
  {
    id: '12',
    type: 'Update',
    title: 'Project created',
    description:
      'Asheera Johnson created Album Rollout - Afrobeat and added you as a collaborator.',
    project: 'Album Rollout - Afrobeat',
    time: '10 days ago',
    isUnread: false,
    color: 'bg-zinc-800 text-zinc-400 border border-white/5',
  },
];

type FilterType =
  | 'All'
  | 'Task'
  | 'Mention'
  | 'Overdue'
  | 'Inventory'
  | 'Invite'
  | 'Budget'
  | 'Milestone';

function NotificationsPage() {
  const { projectId } = Route.useParams();

  const [notifications, setNotifications] = useState<Notification[]>(INITIAL_NOTIFICATIONS);
  const [activeFilter, setActiveFilter] = useState<FilterType>('All');

  const unreadCount = useMemo(() => {
    return notifications.filter((n) => n.isUnread).length;
  }, [notifications]);

  const handleMarkAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isUnread: false })));
  };

  const filteredNotifications = useMemo(() => {
    if (activeFilter === 'All') return notifications;
    return notifications.filter((n) => n.type === activeFilter);
  }, [notifications, activeFilter]);

  const newNotifications = useMemo(() => {
    return filteredNotifications.filter((n) => n.isUnread);
  }, [filteredNotifications]);

  const earlierNotifications = useMemo(() => {
    return filteredNotifications.filter((n) => !n.isUnread);
  }, [filteredNotifications]);

  const getFilterCount = (type: FilterType) => {
    if (type === 'All') return notifications.length;
    return notifications.filter((n) => n.type === type).length;
  };

  const filterTabs: { type: FilterType; label: string }[] = [
    { type: 'All', label: 'All' },
    { type: 'Task', label: 'Tasks' },
    { type: 'Mention', label: 'Mentions' },
    { type: 'Overdue', label: 'Overdue' },
    { type: 'Inventory', label: 'Inventory' },
    { type: 'Invite', label: 'Invites' },
    { type: 'Budget', label: 'Budget' },
    { type: 'Milestone', label: 'Milestones' },
  ];

  return (
    <div className="min-h-screen bg-black relative pb-32">
      <Header />

      <PageLayout>
        <div className="max-w-3xl mx-auto py-6 space-y-6">
          {/* Breadcrumb */}
          <div className="pb-2 border-b border-white/5 flex items-center gap-2 text-xs font-semibold text-zinc-450">
            <Link
              to="/projects/$projectId"
              params={{ projectId: projectId }}
              className="flex items-center gap-2 hover:text-white transition-colors cursor-pointer"
            >
              <FiArrowLeft className="w-4 h-4" />
              <span>Back to Project</span>
            </Link>
            <span className="text-zinc-650">/</span>
            <span className="text-zinc-550">Notifications</span>
          </div>

          {/* Sub-Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-white">Notifications</h2>
              {unreadCount > 0 && (
                <span className="bg-blue-500 text-white text-xs font-bold px-2.5 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="w-full sm:w-auto flex items-center justify-center gap-1.5 px-4 py-2 border border-white/10 hover:bg-white/5 text-zinc-300 hover:text-white rounded-full text-xs font-semibold cursor-pointer transition-all text-center"
              >
                <CheckCheck className="w-4 h-4" />
                <span>Mark all read</span>
              </button>
            )}
          </div>
          <p className="text-zinc-550 text-xs -mt-3">
            Stay on top of tasks, mentions, and project updates.
          </p>

          {/* Filter Pills */}
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 pt-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:overflow-visible sm:flex-wrap">
            {filterTabs.map((tab) => {
              const isSelected = activeFilter === tab.type;
              const count = getFilterCount(tab.type);
              return (
                <button
                  key={tab.type}
                  onClick={() => setActiveFilter(tab.type)}
                  className={`px-4 py-2 rounded-full border text-xs font-semibold transition-all cursor-pointer shrink-0 ${
                    isSelected
                      ? 'bg-white text-black border-white shadow-md'
                      : 'border-white/5 bg-[#0F0F0F]/65 text-zinc-450 hover:text-white hover:border-white/10'
                  }`}
                >
                  {tab.label} {count}
                </button>
              );
            })}
          </div>

          {/* Notifications List */}
          <div className="space-y-6 pt-4">
            {/* NEW Section */}
            {newNotifications.length > 0 && (
              <div className="space-y-3">
                <p className="text-zinc-550 text-[10px] font-bold uppercase tracking-wider">New</p>
                <div className="space-y-3">
                  {newNotifications.map((notif) => (
                    <div
                      key={notif.id}
                      className="bg-[#0F0F0F]/65 border border-white/5 rounded-2xl p-4 flex items-start justify-between gap-3 shadow-md relative overflow-hidden group"
                    >
                      <div className="flex gap-3 min-w-0 flex-1">
                        {/* Status Icon Circle */}
                        <div
                          className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold uppercase shrink-0 ${
                            notif.type === 'Task'
                              ? 'bg-emerald-500/10 text-emerald-400'
                              : notif.type === 'Mention'
                                ? 'bg-blue-500/10 text-blue-400'
                                : notif.type === 'Inventory'
                                  ? 'bg-purple-500/10 text-purple-400'
                                  : 'bg-rose-500/10 text-rose-450'
                          }`}
                        >
                          <FiBell className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white text-xs font-bold truncate sm:whitespace-normal">
                              {notif.title}
                            </span>
                            <span
                              className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full shrink-0 ${notif.color}`}
                            >
                              {notif.type}
                            </span>
                          </div>
                          <p className="text-zinc-300 text-[11px] leading-relaxed mt-1 break-words">
                            {notif.description}
                          </p>
                          <p className="text-zinc-550 text-[10px] mt-2 flex items-center gap-2 font-semibold flex-wrap">
                            <span className="text-blue-450 hover:underline cursor-pointer truncate max-w-[150px] sm:max-w-none">
                              {notif.project}
                            </span>
                            <span>•</span>
                            <span>{notif.time}</span>
                          </p>
                        </div>
                      </div>

                      {/* Unread indicator dot */}
                      <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0 mt-2 self-start animate-pulse" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* EARLIER Section */}
            {earlierNotifications.length > 0 && (
              <div className="space-y-3">
                <p className="text-zinc-550 text-[10px] font-bold uppercase tracking-wider">
                  Earlier
                </p>
                <div className="space-y-3">
                  {earlierNotifications.map((notif) => (
                    <div
                      key={notif.id}
                      className="bg-[#0F0F0F]/45 border border-white/[0.03] hover:border-white/5 rounded-2xl p-4 flex items-start gap-3 transition-all relative overflow-hidden group"
                    >
                      {/* Status Icon Circle */}
                      <div
                        className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold uppercase shrink-0 ${
                          notif.type === 'Task'
                            ? 'bg-emerald-500/10 text-emerald-400'
                            : notif.type === 'Mention'
                              ? 'bg-blue-500/10 text-blue-400'
                              : notif.type === 'Inventory'
                                ? 'bg-purple-500/10 text-purple-400'
                                : notif.type === 'Invite'
                                  ? 'bg-teal-500/10 text-teal-400'
                                  : notif.type === 'Budget'
                                    ? 'bg-amber-500/10 text-amber-400'
                                    : notif.type === 'Milestone'
                                      ? 'bg-indigo-500/10 text-indigo-400'
                                      : 'bg-zinc-800 text-zinc-400'
                        }`}
                      >
                        <FiBell className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-zinc-300 text-xs font-bold truncate sm:whitespace-normal">
                            {notif.title}
                          </span>
                          <span
                            className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full shrink-0 ${notif.color}`}
                          >
                            {notif.type}
                          </span>
                        </div>
                        <p className="text-zinc-450 text-[11px] leading-relaxed mt-1 break-words">
                          {notif.description}
                        </p>
                        <p className="text-zinc-550 text-[10px] mt-2 flex items-center gap-2 flex-wrap">
                          <span className="hover:underline cursor-pointer truncate max-w-[150px] sm:max-w-none">
                            {notif.project}
                          </span>
                          <span>•</span>
                          <span>{notif.time}</span>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {filteredNotifications.length === 0 && (
              <div className="flex flex-col items-center justify-center py-24 text-center text-zinc-500">
                <FiBell className="w-12 h-12 mb-4 text-zinc-700 animate-pulse" />
                <p className="text-sm font-semibold">No notifications in this category</p>
              </div>
            )}
          </div>
        </div>
      </PageLayout>

      <BottomNav />
    </div>
  );
}
